/* eslint-disable import/no-restricted-paths */
import { useApolloClient } from "@apollo/client";
import { useCallback, useMemo, type ComponentProps } from "react";

import { uploadPublicImg } from "@/lib/aws-amplify/host-storage";
import { getURL } from "@/lib/aws-amplify/storage";
import type { ChillnnStudioDriver } from "@/lib/chillnn-studio-driver";
import type { S3Object } from "@/lib/gql/__generated__/graphql";

import type { UseCreateEditorBaseModelProps } from "@/components/features/snack/_shared/model/use-create-editor-base-model";
import { useCreateEditorBaseModel } from "@/components/features/snack/_shared/model/use-create-editor-base-model";

import { s3Repository } from "../../../../_shared/repository/image";
import { imageMastInteractor } from "../../../../_shared/repository/image-mast";

import type { SnackFormModelType } from "../../../create-new-item/model/usecase/use-create-snack-form-model";
import type { Scalars } from "@CHILLNN-Inc/chillnn-abr-core";
import type { SnackS3Object } from "@CHILLNN-Inc/chillnn-abr-snack/dist/entity/type";
import type {
	ChillnnContentsPage,
	IChillnnTemplateCategoryModel,
	IChillnnTemplatePageModel,
	OtherResource,
} from "@CHILLNN-Inc/chillnn-abr-studio";

type Props = {
	hotelID: string;

	snackFormModel: SnackFormModelType;
	editorBaseProps: Omit<UseCreateEditorBaseModelProps, "hotelID">;

	snackList: { snacks: { isPublished: boolean; uuid: string; thumbnail: string; title: string; snack: S3Object }[] };
	hotel: { companyID: string };
};

type ChillnnStudioDriverProps = ComponentProps<typeof ChillnnStudioDriver>;

export const useCreateSnackEditorBaseModel = ({
	hotelID,

	snackFormModel,

	editorBaseProps,

	snackList,
	hotel,
}: Props): ChillnnStudioDriverProps => {
	const companyID = hotel.companyID;
	const { targetSnackItem } = editorBaseProps;
	const { page, ...editorBaseModel } = useCreateEditorBaseModel({
		...editorBaseProps,
		hotelID,
	});

	/**
	 * 他のページへのリンクを設定する際に指定できるリソースを設定します。
	 */
	const pageResources = useMemo<OtherResource[]>(() => {
		return snackList.snacks
			.filter((snack) => snack.isPublished && snack.uuid !== targetSnackItem.uuid)
			.map((snack) => {
				return {
					uuid: snack.uuid,
					thumbnail: snack.thumbnail,
					title: snack.title,
				} satisfies OtherResource;
			});
	}, [snackList.snacks, targetSnackItem.uuid]);

	/**
	 * HPBであれば同一の施設のIDに紐づく施設の別のStaticPageのデータを流し込んでください。
	 * Snackであれば同一の施設のIDに紐づく施設の別のSnackのデータを流し込んでください。
	 * 以下のプロパティでカテゴリを作成してください
	 * uuid: "mypage" title: "マイテンプレート"
	 */
	const templates: IChillnnTemplateCategoryModel = (function () {
		const snacks = snackList.snacks;
		const chillnnTemplateModels: IChillnnTemplatePageModel[] = snacks.map((snack) => {
			return {
				uuid: snack.uuid,
				thumbnail: snack.thumbnail,
				title: snack.title,
				fetchContent: async () =>
					await s3Repository.fetchObject({
						bucket: snack.snack.bucket,
						key: snack.snack.key,
						region: snack.snack.region,
						url: getURL(snack.snack),
					} satisfies SnackS3Object),
			} satisfies IChillnnTemplatePageModel;
		});

		const chillnnTemplateCategory: IChillnnTemplateCategoryModel = {
			uuid: "mypage",
			title: "マイテンプレート",
			fetchTemplates: chillnnTemplateModels,
		};

		return chillnnTemplateCategory;
	})();

	const apolloClient = useApolloClient();

	/**
	 * 新規に画像をアップロードするための関数 Fileを受け取ってS3にアップロードを行い、GraphQLのAPI経由でS3のエイリアスを保存してください。
	 */
	const imageUploader = useCallback<ChillnnStudioDriverProps["editorProps"]["imageUploader"]>(
		async function (image: File): Promise<Scalars["AWSURL"]> {
			const path = `snack/studio/${companyID}/${hotelID}`;
			const fileName = `${new Date().getTime()}.studio`;
			const imageMast = await uploadPublicImg(path, fileName, image);
			await imageMastInteractor(apolloClient).addNewImage(hotelID, imageMast);

			const url = getURL({
				bucket: imageMast.bucket,
				key: imageMast.key,
				region: imageMast.region,
				mimeType: imageMast.mimeType,
				fileName: imageMast.fileName,
			} satisfies S3Object);

			return url;
		},
		[apolloClient, companyID, hotelID],
	);

	/**
	 * STUDIO内で保存ボタンが押された際に呼び出される関数です。 GraphQLのAPIを利用してデータを保存してください。
	 */
	const callbackSave = useCallback<ChillnnStudioDriverProps["editorProps"]["callbackSave"]>(
		async function (): Promise<void> {
			if (page) {
				const path = `snack/studio/${snackFormModel.viewModel.ownerType.value}/${snackFormModel.viewModel.hotelID}/${page.uuid}.${new Date().getTime()}.json`;

				const s3Object = await s3Repository.addJSON<ChillnnContentsPage>(path, page);

				if (!!s3Object.key && !!s3Object.bucket && !!s3Object.region && !!s3Object.url) {
					// 保存したS3ObjectをviewModelにセット（mutable）
					snackFormModel.viewModel.snack = {
						bucket: s3Object.bucket,
						key: s3Object.key,
						region: s3Object.region,
					};

					await snackFormModel.onUpdate();
				}
			}
		},
		[page, snackFormModel],
	);

	return {
		editorProps: {
			...editorBaseModel,

			page,
			imageUploader,

			isShowMultiLang: false,

			pageResources,

			templates,

			// クリック時の、UI上の挙動の違いは各コンポーネント側のhooksで吸収する
			callbackSaveAndExit: callbackSave,
			callbackSave,
		},
	};
};
